
import React, { useEffect, useMemo, useState } from "react";
import {
  CalendarDays,
  Users,
  Palmtree,
  RefreshCw,
  Search,
  RotateCcw,
  Download,
  AlertCircle,
  CheckCircle2,
  Clock3,
  FileSpreadsheet,
  Plus,
  X,
} from "lucide-react";

import apiClient from "../../services/api_client";
import Navbar from "../../components/layout/Navbar";
import Sidebar from "../../components/layout/Sidebar";

const currentYear = new Date().getFullYear();

const formatDays = (value) =>
  Number(value ?? 0).toLocaleString("fr-FR", {
    maximumFractionDigits: 2,
  });

const numberValue = (...values) => {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== "") {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) return parsed;
    }
  }
  return 0;
};

// Lecture stricte des champs séparés N-1 / N renvoyés par l'API.
// IMPORTANT : annual_used et annual_acquired sont des agrégats N-1 + N.
// Ils ne doivent JAMAIS servir de valeurs de secours pour les colonnes N.
const firstNumericValue = (...values) => {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== "") {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) return parsed;
    }
  }
  return null;
};

const getN1Acquired = (row) =>
  firstNumericValue(
    row.n1_acquired,
    row.previous_year_acquired,
    row.annual_previous_acquired,
    row.acquired_n1
  ) ?? 0;

const getNAcquired = (row) =>
  firstNumericValue(
    row.n_acquired,
    row.current_year_acquired,
    row.acquired_n
  ) ?? 0;

const getN1Remaining = (row) => {
  const explicit = firstNumericValue(
    row.n1_remaining,
    row.previous_year_remaining,
    row.annual_previous_remaining,
    row.remaining_n1,
    row.annual_balance_n1
  );
  if (explicit !== null) return Math.max(0, explicit);

  const used = firstNumericValue(
    row.n1_used,
    row.previous_year_used,
    row.annual_previous_used,
    row.used_n1,
    row.annual_debit_n1_total
  );
  return Math.max(0, getN1Acquired(row) - (used ?? 0));
};

const getN1Used = (row) => {
  const explicit = firstNumericValue(
    row.n1_used,
    row.previous_year_used,
    row.annual_previous_used,
    row.used_n1,
    row.annual_debit_n1_total
  );
  if (explicit !== null) return Math.max(0, explicit);

  const remaining = firstNumericValue(
    row.n1_remaining,
    row.previous_year_remaining,
    row.annual_previous_remaining,
    row.remaining_n1,
    row.annual_balance_n1
  );
  return remaining === null
    ? 0
    : Math.max(0, getN1Acquired(row) - remaining);
};

const getNRemaining = (row) => {
  const explicit = firstNumericValue(
    row.n_remaining,
    row.current_year_remaining,
    row.remaining_n,
    row.annual_balance_n
  );
  if (explicit !== null) return Math.max(0, explicit);

  // annual_remaining est un total N-1 + N : on retire le reliquat N-1.
  const total = firstNumericValue(row.annual_remaining, row.annual_total_remaining);
  if (total !== null) {
    return Math.max(0, total - getN1Remaining(row));
  }

  const used = firstNumericValue(
    row.n_used,
    row.current_year_used,
    row.used_n,
    row.annual_debit_n_total
  );
  return Math.max(0, getNAcquired(row) - (used ?? 0));
};

const getNUsed = (row) => {
  const explicit = firstNumericValue(
    row.n_used,
    row.current_year_used,
    row.used_n,
    row.annual_debit_n_total
  );
  if (explicit !== null) return Math.max(0, explicit);

  // Ne pas utiliser row.annual_used : ce champ peut contenir N-1 + N.
  const remaining = firstNumericValue(
    row.n_remaining,
    row.current_year_remaining,
    row.remaining_n,
    row.annual_balance_n
  );
  return remaining === null
    ? 0
    : Math.max(0, getNAcquired(row) - remaining);
};

const getRecoveryAcquired = (row) =>
  numberValue(row.recovery_acquired, row.recovery?.acquired);
const getRecoveryUsed = (row) =>
  numberValue(row.recovery_used, row.recovery?.used);
const getRecoveryRemaining = (row) =>
  numberValue(row.recovery_remaining, row.recovery?.remaining);


const getAnnualTotalRemaining = (row) =>
  Math.round(
    (
      Math.max(0, getN1Remaining(row)) +
      Math.max(0, getNRemaining(row))
    ) * 100
  ) / 100;


const styles = {
  page: {
    padding: "28px",
    minHeight: "calc(100vh - 70px)",
    background: "#f5f7fb",
    color: "#1e293b",
    boxSizing: "border-box",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "18px",
    marginBottom: "26px",
  },
  title: {
    margin: "0 0 8px",
    fontSize: "27px",
    fontWeight: 750,
    color: "#172554",
  },
  subtitle: {
    margin: 0,
    color: "#64748b",
    fontSize: "14px",
  },
  primaryButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "9px",
    padding: "11px 16px",
    border: "none",
    borderRadius: "10px",
    background: "#2563eb",
    color: "#fff",
    fontWeight: 600,
    cursor: "pointer",
  },
  filters: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "14px",
    padding: "18px",
    marginBottom: "24px",
    background: "#fff",
    border: "1px solid #e2e8f0",
    borderRadius: "14px",
  },
  label: {
    display: "block",
    marginBottom: "7px",
    fontSize: "12px",
    fontWeight: 700,
    color: "#64748b",
  },
  input: {
    width: "100%",
    boxSizing: "border-box",
    padding: "11px 12px",
    border: "1px solid #cbd5e1",
    borderRadius: "9px",
    background: "#fff",
    color: "#334155",
    outline: "none",
  },
  stats: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
    gap: "18px",
    marginBottom: "26px",
  },
  statCard: {
    display: "flex",
    alignItems: "flex-start",
    gap: "15px",
    padding: "21px",
    background: "#fff",
    border: "1px solid #e2e8f0",
    borderRadius: "14px",
    boxShadow: "0 3px 12px rgba(15, 23, 42, 0.035)",
  },
  statIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "46px",
    height: "46px",
    borderRadius: "12px",
    flexShrink: 0,
  },
  statLabel: {
    margin: "0 0 9px",
    fontSize: "13px",
    color: "#64748b",
  },
  statValue: {
    margin: 0,
    fontSize: "26px",
    fontWeight: 750,
    color: "#172554",
  },
  card: {
    background: "#fff",
    border: "1px solid #e2e8f0",
    borderRadius: "14px",
    overflow: "hidden",
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "14px",
    padding: "20px 22px",
    borderBottom: "1px solid #e2e8f0",
  },
  sectionTitle: {
    margin: "0 0 5px",
    fontSize: "17px",
    fontWeight: 700,
    color: "#172554",
  },
  muted: {
    margin: 0,
    fontSize: "13px",
    color: "#64748b",
  },
  tableWrapper: {
    width: "100%",
    overflowX: "auto",
  },
  table: {
    width: "100%",
    minWidth: "900px",
    borderCollapse: "collapse",
    textAlign: "left",
  },
  th: {
    padding: "14px 16px",
    background: "#f8fafc",
    color: "#64748b",
    fontSize: "11px",
    fontWeight: 750,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    borderBottom: "1px solid #e2e8f0",
    whiteSpace: "nowrap",
  },
  td: {
    padding: "15px 16px",
    fontSize: "13px",
    borderBottom: "1px solid #edf2f7",
    whiteSpace: "nowrap",
  },
  error: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "15px",
    marginBottom: "20px",
    border: "1px solid #fecaca",
    borderRadius: "10px",
    background: "#fef2f2",
    color: "#b91c1c",
    fontSize: "14px",
  },
  empty: {
    padding: "48px 20px",
    textAlign: "center",
    color: "#64748b",
  },
};

function StatCard({ icon: Icon, label, value, color, background }) {
  return (
    <div style={styles.statCard}>
      <div style={{ ...styles.statIcon, color, background }}>
        <Icon size={23} />
      </div>

      <div>
        <p style={styles.statLabel}>{label}</p>
        <p style={styles.statValue}>{value}</p>
      </div>
    </div>
  );
}

export default function LeaveAnnualReport() {
  const [year, setYear] = useState(currentYear);
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [savingBalance, setSavingBalance] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [initialBalance, setInitialBalance] = useState({
    employee_id: "",
    previous_year: currentYear - 1,
    previous_balance: "0",
    current_year: currentYear,
    current_balance: "0",
    reason: "",
  });

  useEffect(() => {
    let cancelled = false;

    async function loadReport() {
      setLoading(true);
      setError("");

      try {
        const response = await apiClient.get("/leave-reports/annual", {
          params: { year },
        });

        const data = response.data;
        const results = Array.isArray(data)
          ? data
          : data?.results ?? data?.employees ?? data?.data?.results ?? [];

        if (!cancelled) {
          setRows(Array.isArray(results) ? results : []);
        }
      } catch (err) {
        if (!cancelled) {
          setRows([]);

          const status = err.response?.status;
          const detail = err.response?.data?.detail;

          if (status === 404) {
            setError(
              "Le rapport annuel est introuvable côté serveur. Vérifiez la route FastAPI /leave-reports/annual et son enregistrement dans main.py."
            );
          } else {
            setError(
              detail ||
                "Impossible de charger le rapport des congés. Vérifiez la connexion au serveur."
            );
          }
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadReport();

    return () => {
      cancelled = true;
    };
  }, [year, refreshKey]);

  const handleInitialBalanceSubmit = async (event) => {
    event.preventDefault();

    const previousBalance = Number(initialBalance.previous_balance);
    const currentBalance = Number(initialBalance.current_balance);
    const previousYear = Number(initialBalance.previous_year);
    const selectedCurrentYear = Number(initialBalance.current_year);

    if (!initialBalance.employee_id) {
      setError("Veuillez sélectionner un collaborateur.");
      return;
    }

    if (
      !Number.isFinite(previousBalance) ||
      !Number.isFinite(currentBalance) ||
      previousBalance < 0 ||
      currentBalance < 0
    ) {
      setError("Les soldes doivent être des nombres positifs ou nuls.");
      return;
    }

    if (
      !Number.isInteger(previousYear) ||
      !Number.isInteger(selectedCurrentYear) ||
      selectedCurrentYear !== previousYear + 1
    ) {
      setError("L'année N doit être égale à l'année N-1 + 1.");
      return;
    }

    if (!initialBalance.reason.trim()) {
      setError("Veuillez indiquer la justification RH.");
      return;
    }

    setSavingBalance(true);
    setError("");
    setSuccessMessage("");

    try {
      const saveResponse = await apiClient.post(
        "/leave-reports/annual/initial-balance",
        {
          employee_id: Number(initialBalance.employee_id),
          previous_year: previousYear,
          previous_balance: previousBalance,
          current_year: selectedCurrentYear,
          current_balance: currentBalance,
          reason: initialBalance.reason.trim(),
        }
      );

      // Recharger immédiatement l'année correspondante après le POST.
      const reportResponse = await apiClient.get("/leave-reports/annual", {
        params: { year: selectedCurrentYear },
      });

      const data = reportResponse.data;
      const results = Array.isArray(data)
        ? data
        : data?.results ?? data?.employees ?? data?.data?.results ?? [];

      if (!Array.isArray(results)) {
        throw new Error("Format de réponse invalide pour le rapport annuel.");
      }

      const savedRow = results.find(
        (row) => Number(row.employee_id) === Number(initialBalance.employee_id)
      );

      if (!savedRow) {
        throw new Error(
          "Le collaborateur est absent du rapport après l'enregistrement."
        );
      }

      const savedPrevious = getN1Remaining(savedRow);
      const savedCurrent = getNRemaining(savedRow);

      if (
        Math.abs(savedPrevious - previousBalance) > 0.01 ||
        Math.abs(savedCurrent - currentBalance) > 0.01
      ) {
        throw new Error(
          `L'API a répondu mais les soldes du rapport ne correspondent pas. N-1=${formatDays(savedPrevious)} j ; N=${formatDays(savedCurrent)} j. Vérifiez le backend.`
        );
      }

      setYear(selectedCurrentYear);
      setRows(results);
      setModalOpen(false);
      setSuccessMessage(
        saveResponse.data?.message ||
          "Les soldes sont enregistrés et visibles dans le rapport."
      );

      setInitialBalance({
        employee_id: "",
        previous_year: selectedCurrentYear - 1,
        previous_balance: "0",
        current_year: selectedCurrentYear,
        current_balance: "0",
        reason: "",
      });
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          err.message ||
          "Impossible d'enregistrer ou de vérifier les soldes."
      );
    } finally {
      setSavingBalance(false);
    }
  };

  const filteredRows = useMemo(() => {
    const query = search.trim().toLocaleLowerCase("fr");

    if (!query) return rows;

    return rows.filter((row) =>
      String(row.employee_name ?? "")
        .toLocaleLowerCase("fr")
        .includes(query)
    );
  }, [rows, search]);

  const stats = useMemo(() => {
    return filteredRows.reduce(
      (total, row) => ({
        employees: total.employees + 1,
        n1Remaining: total.n1Remaining + getN1Remaining(row),
        nRemaining: total.nRemaining + getNRemaining(row),
        annualUsed: total.annualUsed + getN1Used(row) + getNUsed(row),
        annualRemaining: total.annualRemaining + getN1Remaining(row) + getNRemaining(row),
        recoveryAcquired:
          total.recoveryAcquired + getRecoveryAcquired(row),
        recoveryUsed:
          total.recoveryUsed + getRecoveryUsed(row),
        recoveryRemaining:
          total.recoveryRemaining + getRecoveryRemaining(row),
      }),
      {
        employees: 0,
        n1Remaining: 0,
        nRemaining: 0,
        annualUsed: 0,
        annualRemaining: 0,
        recoveryAcquired: 0,
        recoveryUsed: 0,
        recoveryRemaining: 0,
      }
    );
  }, [filteredRows]);

  const exportCsv = () => {
    if (!filteredRows.length) return;

    const headers = [
      "Employé",
      `Congés acquis N-1 (${year - 1})`,
      `Congés pris N-1 (${year - 1})`,
      `Solde restant N-1 (${year - 1})`,
      `Congés acquis N (${year})`,
      `Congés pris N (${year})`,
      `Solde restant N (${year})`,
      "Solde total annuel restant",
      "Récupérations attribuées",
      "Récupérations utilisées",
      "Récupérations restantes",
    ];

    const escapeCsv = (value) =>
      `"${String(value ?? "").replace(/"/g, '""')}"`;

    const csvRows = [
      headers,
      ...filteredRows.map((row) => [
        row.employee_name,
        getN1Acquired(row),
        getN1Used(row),
        getN1Remaining(row),
        getNAcquired(row),
        getNUsed(row),
        getNRemaining(row),
        getAnnualTotalRemaining(row),
        getRecoveryAcquired(row),
        getRecoveryUsed(row),
        getRecoveryRemaining(row),
      ]),
    ];

    const csvContent =
      "\uFEFF" + csvRows.map((row) => row.map(escapeCsv).join(";")).join("\r\n");

    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = `rapport-conges-${year}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const years = Array.from({ length: 7 }, (_, index) => currentYear - 5 + index);

  return (
    <div
      style={{
        display: "flex",
        minHeight: "100vh",
        width: "100%",
        background: "#f5f7fb",
      }}
    >
      <Sidebar />

      <div
        style={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Navbar />

        <main style={styles.page}>
          <div style={styles.header}>
            <div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "9px",
                  marginBottom: "10px",
                  color: "#2563eb",
                  fontSize: "12px",
                  fontWeight: 700,
                }}
              >
                <CalendarDays size={17} />
                GESTION DES RESSOURCES HUMAINES
              </div>

              <h1 style={styles.title}>Rapport annuel des congés</h1>

              <p style={styles.subtitle}>
                Consultez les soldes, les jours acquis et les congés utilisés
                par employé.
              </p>
            </div>

           <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
  <button
    type="button"
    onClick={() => {
      setError("");
      setSuccessMessage("");
      setModalOpen(true);
    }}
    style={styles.primaryButton}
  >
    <Plus size={17} />
    Initialiser un solde
  </button>

  <button
    type="button"
    onClick={() => setRefreshKey((value) => value + 1)}
    disabled={loading}
    style={{
      ...styles.primaryButton,
      opacity: loading ? 0.65 : 1,
    }}
  >
    <RotateCcw size={17} />
    Actualiser
  </button>
</div>
          </div>

          <section style={styles.filters}>
            <div style={{ minWidth: "150px" }}>
              <label htmlFor="report-year" style={styles.label}>
                ANNÉE DU RAPPORT
              </label>

              <select
                id="report-year"
                value={year}
                onChange={(event) => setYear(Number(event.target.value))}
                style={styles.input}
              >
                {years.map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ flex: 1, minWidth: "220px" }}>
              <label htmlFor="employee-search" style={styles.label}>
                RECHERCHER UN EMPLOYÉ
              </label>

              <div style={{ position: "relative" }}>
                <Search
                  size={18}
                  style={{
                    position: "absolute",
                    left: "12px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "#94a3b8",
                  }}
                />

                <input
                  id="employee-search"
                  type="search"
                  placeholder="Nom de l'employé..."
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  style={{ ...styles.input, paddingLeft: "40px" }}
                />
              </div>
            </div>

            <div style={{ color: "#64748b", fontSize: "13px" }}>
              {filteredRows.length} employé(s)
            </div>
          </section>

          {error && (
            <div role="alert" style={styles.error}>
              <AlertCircle size={20} />
              <span>{error}</span>
            </div>
          )}

          

          <section style={styles.card}>
            <div style={styles.cardHeader}>
              <div>
                <h2 style={styles.sectionTitle}>
                  <FileSpreadsheet
                    size={18}
                    style={{
                      display: "inline",
                      verticalAlign: "middle",
                      marginRight: "8px",
                    }}
                  />
                  Détail des soldes — {year}
                </h2>

                <p style={styles.muted}>
                  Synthèse des congés annuels et des récupérations.
                </p>
              </div>

              <button
                type="button"
                onClick={exportCsv}
                disabled={!filteredRows.length || loading}
                style={{
                  ...styles.primaryButton,
                  background: "#fff",
                  color: "#334155",
                  border: "1px solid #cbd5e1",
                  opacity: !filteredRows.length || loading ? 0.5 : 1,
                }}
              >
                <Download size={17} />
                Exporter CSV
              </button>
            </div>

            {loading ? (
              <div style={styles.empty}>
                <Clock3
                  size={30}
                  style={{ marginBottom: "12px", color: "#2563eb" }}
                />
                <p>Chargement du rapport annuel...</p>
              </div>
            ) : error ? (
              <div style={styles.empty}>
                <AlertCircle
                  size={30}
                  style={{ marginBottom: "12px", color: "#dc2626" }}
                />
                <p>Les données ne sont pas disponibles.</p>
                <p style={styles.muted}>
                  Corrigez la route API pour afficher le rapport.
                </p>
              </div>
            ) : (
              <div style={styles.tableWrapper}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th rowSpan={2} style={styles.th}>Employé</th>
                      <th colSpan={3} style={{ ...styles.th, textAlign: "center", color: "#7c3aed" }}>
                        Congés N-1 ({year - 1})
                      </th>
                      <th colSpan={3} style={{ ...styles.th, textAlign: "center", color: "#047857" }}>
                        Congés N ({year})
                      </th>
                      <th rowSpan={2} style={{ ...styles.th, color: "#1d4ed8" }}>Solde total (j)</th>
                      <th colSpan={3} style={{ ...styles.th, textAlign: "center", color: "#b45309" }}>
                        Récupérations
                      </th>
                    </tr>
                    <tr>
                      {[
                        "Acquis", "Pris", "Restant",
                        "Acquis", "Pris", "Restant",
                        "Attribué", "Utilisé", "Restant",
                      ].map((label, index) => (
                        <th key={`${label}-${index}`} style={styles.th}>
                          {label} (j)
                        </th>
                      ))}
                    </tr>
                  </thead>

                  <tbody>
                    {filteredRows.map((row, index) => (
                      <tr
                        key={row.employee_id ?? `${row.employee_name}-${index}`}
                        onMouseEnter={(event) => {
                          event.currentTarget.style.background = "#f8fafc";
                        }}
                        onMouseLeave={(event) => {
                          event.currentTarget.style.background = "#fff";
                        }}
                      >
                        <td style={{ ...styles.td, fontWeight: 650 }}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "10px",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: "34px",
                                height: "34px",
                                borderRadius: "50%",
                                background: "#dbeafe",
                                color: "#1d4ed8",
                                fontWeight: 750,
                                flexShrink: 0,
                              }}
                            >
                              {String(row.employee_name ?? "?")
                                .trim()
                                .charAt(0)
                                .toUpperCase()}
                            </div>
                            {row.employee_name ?? "Employé sans nom"}
                          </div>
                        </td>

                        <td style={styles.td}>{formatDays(getN1Acquired(row))}</td>
                        <td style={styles.td}>{formatDays(getN1Used(row))}</td>
                        <td style={{ ...styles.td, fontWeight: 750, color: getN1Remaining(row) < 0 ? "#dc2626" : "#7c3aed" }}>
                          {formatDays(getN1Remaining(row))}
                        </td>

                        <td style={styles.td}>{formatDays(getNAcquired(row))}</td>
                        <td style={styles.td}>{formatDays(getNUsed(row))}</td>
                        <td style={{ ...styles.td, fontWeight: 750, color: getNRemaining(row) < 0 ? "#dc2626" : "#047857" }}>
                          {formatDays(getNRemaining(row))}
                        </td>

                        <td style={{ ...styles.td, fontWeight: 800, color: getAnnualTotalRemaining(row) < 0 ? "#dc2626" : "#1d4ed8" }}>
                          {formatDays(getAnnualTotalRemaining(row))}
                        </td>

                        <td style={styles.td}>
                          {formatDays(getRecoveryAcquired(row))}
                        </td>
                        <td style={styles.td}>
                          {formatDays(getRecoveryUsed(row))}
                        </td>
                        <td
                          style={{
                            ...styles.td,
                            fontWeight: 750,
                            color:
                              getRecoveryRemaining(row) < 0
                                ? "#dc2626"
                                : "#b45309",
                          }}
                        >
                          {formatDays(getRecoveryRemaining(row))}
                        </td>
                      </tr>
                    ))}

                    {filteredRows.length === 0 && (
                      <tr>
                        <td colSpan={12} style={styles.empty}>
                          <Users
                            size={30}
                            style={{ marginBottom: "12px" }}
                          />
                          <p>
                            {rows.length === 0
                              ? "Aucun employé trouvé pour cette année."
                              : "Aucun employé ne correspond à votre recherche."}
                          </p>
                        </td>
                      </tr>
                    )}
                  </tbody>

                 
                </table>
              </div>
            )}

            <div
              style={{
                padding: "15px 22px",
                borderTop: "1px solid #e2e8f0",
                color: "#64748b",
                fontSize: "12px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <CalendarDays size={15} />
              Année sélectionnée : {year}
              <span style={{ marginLeft: "auto" }}>
                {loading ? "Actualisation..." : "Rapport RH"}
              </span>
            </div>
          </section>

          {successMessage && (
  <div
    role="status"
    style={{
      marginTop: "16px",
      padding: "14px",
      borderRadius: "10px",
      background: "#dcfce7",
      color: "#166534",
    }}
  >
    <CheckCircle2
      size={18}
      style={{ verticalAlign: "middle", marginRight: "8px" }}
    />
    {successMessage}
  </div>
)}

{modalOpen && (
  <div
    onMouseDown={(event) => {
      if (event.target === event.currentTarget && !savingBalance) {
        setModalOpen(false);
      }
    }}
    style={{
      position: "fixed",
      inset: 0,
      zIndex: 1000,
      background: "rgba(15, 23, 42, 0.55)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "20px",
      overflowY: "auto",
    }}
  >
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="initial-balance-title"
      style={{
        width: "100%",
        maxWidth: "620px",
        background: "#fff",
        borderRadius: "16px",
        padding: "24px",
        boxSizing: "border-box",
        boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "12px",
          marginBottom: "20px",
        }}
      >
        <div>
          <h2
            id="initial-balance-title"
            style={{ margin: "0 0 6px", color: "#172554" }}
          >
            Initialisation du solde de congés
          </h2>
          <p style={styles.muted}>
            Saisissez les soldes restants validés par les RH.
          </p>
        </div>

        <button
          type="button"
          aria-label="Fermer"
          disabled={savingBalance}
          onClick={() => setModalOpen(false)}
          style={{
            border: "none",
            background: "#f1f5f9",
            borderRadius: "8px",
            padding: "8px",
            cursor: "pointer",
          }}
        >
          <X size={20} />
        </button>
      </div>

      <form onSubmit={handleInitialBalanceSubmit}>
        <div style={{ marginBottom: "16px" }}>
          <label htmlFor="initial-employee" style={styles.label}>
            COLLABORATEUR
          </label>

          <select
            id="initial-employee"
            required
            value={initialBalance.employee_id}
            onChange={(event) =>
              setInitialBalance((previous) => ({
                ...previous,
                employee_id: event.target.value,
              }))
            }
            style={styles.input}
          >
            <option value="">Sélectionner un collaborateur</option>

            {rows
              .filter((row) => row.employee_id != null)
              .filter(
                (row, index, array) =>
                  array.findIndex(
                    (item) => item.employee_id === row.employee_id
                  ) === index
              )
              .map((row) => (
                <option key={row.employee_id} value={row.employee_id}>
                  {row.employee_name} — ID {row.employee_id}
                </option>
              ))}
          </select>

          <p style={{ ...styles.muted, marginTop: "6px" }}>
            La liste provient des collaborateurs renvoyés par le rapport.
          </p>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
            gap: "16px",
          }}
        >
          <div>
            <label htmlFor="initial-previous-year" style={styles.label}>
              ANNÉE N-1
            </label>
            <input
              id="initial-previous-year"
              type="number"
              required
              value={initialBalance.previous_year}
              onChange={(event) =>
                setInitialBalance((previous) => ({
                  ...previous,
                  previous_year: event.target.value,
                }))
              }
              style={styles.input}
            />
          </div>

          <div>
            <label htmlFor="initial-previous-balance" style={styles.label}>
              SOLDE RESTANT N-1 (JOURS)
            </label>
            <input
              id="initial-previous-balance"
              type="number"
              min="0"
              step="0.25"
              required
              value={initialBalance.previous_balance}
              onChange={(event) =>
                setInitialBalance((previous) => ({
                  ...previous,
                  previous_balance: event.target.value,
                }))
              }
              style={styles.input}
            />
          </div>

          <div>
            <label htmlFor="initial-current-year" style={styles.label}>
              ANNÉE N
            </label>
            <input
              id="initial-current-year"
              type="number"
              required
              value={initialBalance.current_year}
              onChange={(event) =>
                setInitialBalance((previous) => ({
                  ...previous,
                  current_year: event.target.value,
                }))
              }
              style={styles.input}
            />
          </div>

          <div>
            <label htmlFor="initial-current-balance" style={styles.label}>
              SOLDE RESTANT N (JOURS)
            </label>
            <input
              id="initial-current-balance"
              type="number"
              min="0"
              step="0.25"
              required
              value={initialBalance.current_balance}
              onChange={(event) =>
                setInitialBalance((previous) => ({
                  ...previous,
                  current_balance: event.target.value,
                }))
              }
              style={styles.input}
            />
          </div>
        </div>

        <div style={{ marginTop: "16px" }}>
          <label htmlFor="initial-reason" style={styles.label}>
            JUSTIFICATION RH
          </label>
          <textarea
            id="initial-reason"
            required
            rows={3}
            maxLength={1000}
            placeholder="Ex. : reprise du solde validé avant la digitalisation..."
            value={initialBalance.reason}
            onChange={(event) =>
              setInitialBalance((previous) => ({
                ...previous,
                reason: event.target.value,
              }))
            }
            style={{
              ...styles.input,
              resize: "vertical",
              fontFamily: "inherit",
            }}
          />
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "10px",
            marginTop: "24px",
            flexWrap: "wrap",
          }}
        >
          <button
            type="button"
            disabled={savingBalance}
            onClick={() => setModalOpen(false)}
            style={{
              ...styles.primaryButton,
              background: "#e2e8f0",
              color: "#334155",
            }}
          >
            Annuler
          </button>

          <button
            type="submit"
            disabled={savingBalance}
            style={{
              ...styles.primaryButton,
              opacity: savingBalance ? 0.65 : 1,
            }}
          >
            {savingBalance ? "Enregistrement..." : "Enregistrer le solde"}
          </button>
        </div>
      </form>
    </div>
  </div>
)}
        </main>

      </div>
    </div>
  );
}
